import React from "react";
import { useTranslation } from "react-i18next";
import FullModal from "components/FullModal";
import { Grid, Button, makeStyles, Theme } from "@material-ui/core";
import FormInput from "../FormInput";
import { User } from "data/api/models";
import useTKey from "utils/useTKey";
import { object, string } from "yup";
import { Formik } from "formik";
import asyncAction from "utils/asyncAction";
import { useCurrentUser } from "data/user";
import { useSnackbar } from "data/snackbar/hooks";

interface ManageChecklistProps {
  onClose: () => void;
  user: User;
}

const useStyles = makeStyles((theme: Theme) => ({
  fullHeight: {
    height: "100%",
  },
}));

const phoneRegExp =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

const useValidationSchema = () => {
  const t = useTKey("common.error.form");

  return object({
    firstName: string().required(t("required")),
    lastName: string().required(t("required")),
    phoneNumber: string().matches(phoneRegExp, t("phoneNumber")).required(t("required")),
    email: string().email(t("email")).required(t("required")),
  });
};

const UserProfile = ({ onClose, user }: ManageChecklistProps) => {
  const [t] = useTranslation();
  const styles = useStyles();
  const schema = useValidationSchema();
  const [, { editUserProfile }] = useCurrentUser();
  const [, { push }] = useSnackbar();

  return (
    <FullModal title={t("userProfile.title")} onClose={onClose}>
      <Formik
        initialValues={{
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          phoneNumber: user.phoneNumber || "",
          email: user.email || "",
        }}
        validationSchema={schema}
        onSubmit={(values) => {
          asyncAction(() =>
            editUserProfile({
              id: user.id!,
              updateUserRequest: {
                id: user.id!,
                firstName: values.firstName,
                lastName: values.lastName,
                phoneNumber: values.phoneNumber,
                email: values.email,
              },
            })
          )
            .then((updatedUser) => {
              onClose();
              push({ message: t("userProfile.updateSuccess") });
            })
            .catch(() => {
              push({ message: t("userProfile.updateFailed") });
            });
        }}
      >
        {({ values, errors, handleChange, handleSubmit, dirty }) => (
          <Grid container justify="space-between" direction="column" className={styles.fullHeight}>
            <Grid container spacing={4} direction="column">
              <Grid item xs={4}>
                <FormInput
                  name="firstName"
                  error={errors.firstName}
                  value={values.firstName}
                  label={t("userProfile.firstName")}
                  onChange={handleChange}
                />
                <FormInput
                  name="lastName"
                  error={errors.lastName}
                  value={values.lastName}
                  label={t("userProfile.lastName")}
                  onChange={handleChange}
                />
                <FormInput
                  name="phoneNumber"
                  error={errors.phoneNumber}
                  value={values.phoneNumber}
                  label={t("userProfile.phoneNumber")}
                  onChange={handleChange}
                />
                <FormInput
                  name="email"
                  error={errors.email}
                  value={values.email}
                  label={t("userProfile.email")}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            <Grid item>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                onClick={() => handleSubmit()}
                disabled={!dirty}
              >
                {t("common.save")}
              </Button>
            </Grid>
          </Grid>
        )}
      </Formik>
    </FullModal>
  );
};

export default UserProfile;
